<script setup lang="ts">
import ReviewStars from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/ReviewStars.vue';
import GridButton from '@zyro-inc/site-modules/components/elements/button/GridButton.vue';
import IconStar from '@zyro-inc/site-modules/components/icons/IconStar.vue';
import { computed } from 'vue';

const emit = defineEmits<{
	'rating-click': [number | null],
	'leave-review-click': [],
}>();

const props = defineProps<{
	averageRating: number,
	totalReviews: number,
	ratingCounts: Array<number>,
	translations: Record<string, string>,
	blockButtonType: string,
	blockButtonBorderWidth: number,
	buttonBorderColorHover: string,
	buttonBorderColor: string,
	buttonBackgroundColor: string,
	buttonBackgroundColorHover: string,
	filteredRating: number | null,
}>();

const calculateBarWidth = (count: number) => {
	if (props.totalReviews === 0) return 0;

	return (count / props.totalReviews) * 100;
};

const reviewText = computed(() => (props.totalReviews === 1 ? props.translations.reviewSingular : props.translations.reviewsPlural));
</script>

<template>
	<div class="product-review-analytics">
		<div class="product-review-analytics__left-column">
			<div class="product-review-analytics__total-reviews-wrapper">
				<h3 class="product-review-analytics__average-rating">
					{{ averageRating.toFixed(1) }}
				</h3>
				<div>
					<ReviewStars
						class="product-review-analytics__rating-stars"
						:rating="averageRating"
						is-half-allowed
					/>
					<p>
						{{ props.totalReviews }} {{ reviewText }}
					</p>
				</div>
			</div>
			<GridButton
				v-qa="`productsection-btn-leavereview`"
				:content="translations.reviewButton || 'Leave review'"
				:type="blockButtonType"
				:border-width="blockButtonBorderWidth"
				:border-color="buttonBorderColor"
				:background-color="buttonBackgroundColor"
				:border-color-hover="buttonBorderColorHover"
				:background-color-hover="buttonBackgroundColorHover"
				tag-name="button"
				@click="emit('leave-review-click')"
			/>
		</div>
		<div class="product-review-analytics__right-column">
			<div class="product-review-analytics__grid">
				<div
					v-for="i in 5"
					:key="i"
					class="product-review-analytics__row"
					:class="{ 'product-review-analytics__row--active': filteredRating === ratingCounts.length - i + 1 }"
					@click="emit('rating-click', ratingCounts.length - i + 1)"
				>
					<IconStar class="product-review-analytics__star" />
					<p class="product-review-analytics__rating-label">
						{{ 5 - i + 1 }}
					</p>
					<div class="product-review-analytics__bar-container">
						<div
							:data-qa="`product-review-analytics-rating-bar-${5 - i + 1}`"
							class="product-review-analytics__rating-bar"
							:style="{
								width: calculateBarWidth(ratingCounts[5 - i]) + '%',
								backgroundColor: 'currentColor'
							}"
						/>
					</div>
					<p class="product-review-analytics__rating-count">
						{{ ratingCounts[ratingCounts.length - i] }}
					</p>
					<a
						v-if="filteredRating && filteredRating === ratingCounts.length - i + 1"
						data-qa="productsection-btn-clearfilter"
						class="product-review-analytics__clear-filter-button"
						@click.stop="emit('rating-click', null)"
					>
						{{ translations.clearFilter }}
					</a>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/font-style";
@include font-style("h3", ".product-review-analytics__average-rating", ".product-review-analytics");

.product-review-analytics {
	$this: &;

	display: flex;
	gap: 16px;
	padding-bottom: 80px;
	color: var(--body-color);

	&__total-reviews-wrapper {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 16px;
	}

	&__rating-stars {
		margin-bottom: 4px;
	}

	&__left-column {
		margin-right: 100px;
	}

	&__right-column {
		width: 100%;
	}

	&__grid {
		display: grid;
		gap: 8px;
	}

	&__star {
		display: flex;
		align-items: center;
	}

	&__rating-label,
	&__rating-count {
		text-align: center;
	}

	&__bar-container {
		height: 16px;
		background-color: $color-light;
		border-radius: 4px;
		overflow: hidden;
		max-width: 238px;
		width: 100%;
		border: 1px solid $color-gray-border;
	}

	&__row {
		display: grid;
		grid-template-columns: 1fr 1fr 12fr 1fr 8fr;
		align-items: center;
		gap: 8px;
		cursor: pointer;

		&--active {
			#{$this}__bar-container {
				border-color: var(--body-color);
			}
		}

		&:hover {
			#{$this}__bar-container {
				border-color: var(--body-color);
			}
		}
	}

	&__rating-bar {
		height: 100%;
		transition: width 0.3s ease;
	}

	&__right-column {
		max-width: 526px;
	}

	&__clear-filter-button {
		text-decoration: underline;
	}
}

@include site-engine-mobile {
	.product-review-analytics {
		flex-direction: column;

		&__left-column {
			margin-right: 0;
		}
	}
}
</style>
